import React from 'react';
import { Mail, MessageSquare, Instagram, Facebook, Phone, MoreHorizontal } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface InboxContentProps {
  animationDelay?: number;
}

const conversations = [
  { 
    name: 'Sarah Johnson', 
    message: 'Hi! I saw your product demo and...', 
    time: '2m', 
    unread: true, 
    platform: 'email',
    avatar: 'SJ'
  },
  { 
    name: 'Mike Chen', 
    message: 'Can we schedule a call tomorrow?', 
    time: '15m', 
    unread: true, 
    platform: 'sms',
    avatar: 'MC'
  },
  { 
    name: 'Emily Davis', 
    message: 'Thanks for the quick response!', 
    time: '1h', 
    unread: false, 
    platform: 'instagram',
    avatar: 'ED'
  },
  { 
    name: 'Alex Turner', 
    message: 'I have a question about pricing...', 
    time: '2h', 
    unread: false, 
    platform: 'facebook',
    avatar: 'AT'
  },
  { 
    name: 'Lisa Park', 
    message: 'Great working with you!', 
    time: '3h', 
    unread: false, 
    platform: 'email',
    avatar: 'LP'
  },
];

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'email': return Mail;
    case 'sms': return Phone;
    case 'instagram': return Instagram;
    case 'facebook': return Facebook;
    default: return MessageSquare;
  }
};

const getPlatformColor = (platform: string) => {
  switch (platform) {
    case 'email': return '#3B82F6';
    case 'sms': return '#10B981';
    case 'instagram': return '#E4405F';
    case 'facebook': return '#1877F2';
    default: return '#6B7280';
  }
};

const InboxContent: React.FC<InboxContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex-1 flex overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Conversation List */}
      <div 
        className="w-1/2 lg:w-1/2 border-r"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF'
        }}
      >
        {/* Header */}
        <div 
          className="px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border-b flex items-center justify-between"
          style={{ 
            borderColor: isDark ? '#333333' : '#E5E5E5',
            animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both`
          }}
        >
          <span 
            className="text-[8px] sm:text-[10px] lg:text-xs font-semibold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            Inbox
          </span>
          <div 
            className="px-1 lg:px-1.5 py-0.5 rounded-full text-[6px] sm:text-[7px] lg:text-[9px] font-medium"
            style={{ 
              backgroundColor: '#3B82F6',
              color: '#FFFFFF'
            }}
          >
            2
          </div>
        </div>

        {/* Conversations */}
        <div className="overflow-hidden">
          {conversations.slice(0, 4).map((convo, index) => {
            const Icon = getPlatformIcon(convo.platform);
            const color = getPlatformColor(convo.platform);
            
            return (
              <div
                key={index}
                className="px-1 sm:px-1.5 lg:px-3 py-1 sm:py-1.5 lg:py-2 border-b cursor-pointer transition-colors"
                style={{ 
                  borderColor: isDark ? '#333333' : '#E5E5E5',
                  backgroundColor: index === 0 
                    ? (isDark ? '#252525' : '#F5F5F5')
                    : 'transparent',
                  animation: `slideInFromLeft 0.3s ease-out ${animationDelay + 200 + (index * 60)}ms both`
                }}
              >
                <div className="flex items-start gap-1 lg:gap-2">
                  <div 
                    className="w-4 h-4 sm:w-5 sm:h-5 lg:w-7 lg:h-7 rounded-full flex items-center justify-center text-[5px] sm:text-[7px] lg:text-[9px] font-medium flex-shrink-0"
                    style={{ 
                      backgroundColor: isDark ? '#333333' : '#E5E5E5',
                      color: isDark ? '#FFFFFF' : '#111111'
                    }}
                  >
                    {convo.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span 
                        className="text-[6px] sm:text-[8px] lg:text-[10px] font-medium"
                        style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                      >
                        {convo.name.split(' ')[0]}
                      </span>
                      <span 
                        className="text-[5px] sm:text-[6px] lg:text-[8px] flex-shrink-0"
                        style={{ color: isDark ? '#666666' : '#999999' }}
                      >
                        {convo.time}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 lg:gap-1 mt-0.5">
                      <Icon className="w-1.5 h-1.5 sm:w-2 sm:h-2 lg:w-2.5 lg:h-2.5 flex-shrink-0" style={{ color }} />
                      <span 
                        className="text-[5px] sm:text-[7px] lg:text-[9px] hidden sm:block"
                        style={{ 
                          color: convo.unread 
                            ? (isDark ? '#CCCCCC' : '#333333')
                            : (isDark ? '#666666' : '#999999'),
                          fontWeight: convo.unread ? 500 : 400
                        }}
                      >
                        {convo.message.slice(0, 20)}...
                      </span>
                      {convo.unread && (
                        <div 
                          className="w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: '#3B82F6' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Message Preview */}
      <div 
        className="w-1/2 flex flex-col"
        style={{ backgroundColor: isDark ? '#222222' : '#F9F9F9' }}
      >
        {/* Message Header */}
        <div 
          className="px-2 sm:px-3 py-1 sm:py-1.5 lg:py-2 border-b flex items-center justify-between"
          style={{ 
            borderColor: isDark ? '#333333' : '#E5E5E5',
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            animation: `slideInFromTop 0.3s ease-out ${animationDelay + 300}ms both`
          }}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <div 
              className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full flex items-center justify-center text-[6px] sm:text-[8px] font-medium"
              style={{ 
                backgroundColor: isDark ? '#333333' : '#E5E5E5',
                color: isDark ? '#FFFFFF' : '#111111'
              }}
            >
              SJ
            </div>
            <div>
              <div 
                className="text-[7px] sm:text-[9px] lg:text-[10px] font-medium"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                Sarah J.
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1">
                <Mail className="w-1.5 h-1.5 sm:w-2 sm:h-2" style={{ color: '#3B82F6' }} />
                <span className="text-[5px] sm:text-[7px] lg:text-[8px]" style={{ color: isDark ? '#666666' : '#999999' }}>
                  Email
                </span>
              </div>
            </div>
          </div>
          <MoreHorizontal className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: isDark ? '#666666' : '#999999' }} />
        </div>

        {/* Message Content */}
        <div 
          className="flex-1 p-1.5 sm:p-2 lg:p-3"
          style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 400}ms both` }}
        >
          <div 
            className="p-1.5 sm:p-2 rounded-lg max-w-[90%]"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <p className="text-[7px] sm:text-[9px] lg:text-[10px]" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>
              Hi! I saw your demo and I'm impressed...
            </p>
            <span 
              className="text-[5px] sm:text-[7px] lg:text-[8px] mt-1 block"
              style={{ color: isDark ? '#666666' : '#999999' }}
            >
              2 min ago
            </span>
          </div>
        </div>

        {/* Reply Input */}
        <div 
          className="p-1.5 sm:p-2 border-t"
          style={{ 
            borderColor: isDark ? '#333333' : '#E5E5E5',
            animation: `slideInFromBottom 0.3s ease-out ${animationDelay + 500}ms both`
          }}
        >
          <div 
            className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-1 sm:py-1.5 rounded-lg"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <span 
              className="text-[7px] sm:text-[9px] lg:text-[10px] flex-1"
              style={{ color: isDark ? '#666666' : '#999999' }}
            >
              Type...
            </span>
            <div 
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[6px] sm:text-[8px] lg:text-[9px] font-medium"
              style={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }}
            >
              Send
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboxContent;
